import React, { useState } from 'react';
import './BulkPricingSection.css';
import { PriceTier } from '../../types';
import { SizeQuantity } from '@flooron/shared';

interface BulkPricingSectionProps {
  priceTiers: PriceTier[];
  currentQuantity: number;
  currentPrice?: number;
  quantity?: number;
  totalPrice?: number;
  sizeQuantities?: SizeQuantity[];
  availableQuantity?: number;
  sizeQuantitiesState?: Record<string, number>;
  generalQuantity?: number;
  onGeneralQuantityChange?: (qty: number) => void;
  onSizeQuantityChange?: (size: string, qty: number) => void;
  getPriceForQuantity?: (qty: number) => number;
}

const BulkPricingSection: React.FC<BulkPricingSectionProps> = ({ 
  priceTiers, 
  currentQuantity,
  currentPrice,
  quantity,
  totalPrice,
  sizeQuantities,
  availableQuantity,
  sizeQuantitiesState,
  generalQuantity,
  onGeneralQuantityChange,
  onSizeQuantityChange,
  getPriceForQuantity
}) => {
  const [selectedSize, setSelectedSize] = useState<string>(sizeQuantities?.[0]?.size || '');
  if (!priceTiers || priceTiers.length <= 0) return null;

  return (
    <div className="bulk-pricing-card">
      {/* Price Spotlight - moved from CartActionsSection */}
      {currentPrice !== undefined && totalPrice !== undefined && (
        <>
          <div className="price-spotlight">
            <div className="price-main">
              <span className="currency-symbol">₹</span>
              <span className="amount-big">{currentPrice.toLocaleString()}</span>
              <span className="per-unit">/unit</span>
            </div>
            <div className="price-subtext">
              <span>Total:</span>
              <strong>₹{totalPrice.toLocaleString()}</strong>
            </div>
          </div>
          <div className="divider-line" />
        </>
      )}

      {/* Size Selector - moved from CartActionsSection */}
      {sizeQuantities && sizeQuantities.length > 0 && (
        <>
          <div className="size-selector-container">
            <label className="size-label">Select Size</label>
            <div className="size-options">
              {sizeQuantities.map((item, index) => (
                <button
                  key={index}
                  className={`size-option ${selectedSize === item.size ? 'active' : ''}`}
                  onClick={() => setSelectedSize(item.size)}
                >
                  {item.size}
                </button>
              ))}
            </div>
          </div>
          <div className="divider-line" />
        </>
      )}

      {/* Quantity Adjuster & Stock Status - moved from CartActionsSection */}
      {sizeQuantities && sizeQuantities.length > 0 && sizeQuantitiesState && onSizeQuantityChange && (
        <>
          <div className="quantity-stock-row">
            <div className="qty-picker">
              <button 
                onClick={() => {
                  const currentQty = sizeQuantitiesState[selectedSize] || 0;
                  onSizeQuantityChange(selectedSize, Math.max(0, currentQty - 1));
                }} 
                disabled={!selectedSize || (sizeQuantitiesState[selectedSize] || 0) <= 0}
              >
                –
              </button>
              <input 
                type="number" 
                className="qty-input" 
                value={sizeQuantitiesState[selectedSize] || 0}
                onChange={(e) => {
                  if (selectedSize) {
                    onSizeQuantityChange(selectedSize, Math.max(0, parseInt(e.target.value) || 0));
                  }
                }}
                min="0"
              />
              <button 
                onClick={() => {
                  const currentQty = sizeQuantitiesState[selectedSize] || 0;
                  onSizeQuantityChange(selectedSize, currentQty + 1);
                }} 
                disabled={!selectedSize}
              >
                +
              </button>
            </div>
            <div className={`stock-tag ${availableQuantity && availableQuantity > 0 ? 'is-in-stock' : 'is-out'}`}>
              {availableQuantity && availableQuantity > 0 ? `${availableQuantity} left` : 'Out of Stock'}
            </div>
          </div>
          <div className="divider-line" />
        </>
      )}

      {!sizeQuantities?.length && generalQuantity !== undefined && onGeneralQuantityChange && (
        <>
          <div className="quantity-stock-row">
            <div className="qty-picker">
              <button
                onClick={() => onGeneralQuantityChange(Math.max(1, generalQuantity - 1))}
                disabled={generalQuantity <= 1}
              >
                –
              </button>
              <input
                type="number"
                className="qty-input"
                value={generalQuantity}
                min="1"
                onChange={(e) => onGeneralQuantityChange(Math.max(1, parseInt(e.target.value) || 1))}
              />
              <button
                onClick={() => onGeneralQuantityChange(generalQuantity + 1)}
                disabled={availableQuantity !== undefined && generalQuantity >= availableQuantity}
              >
                +
              </button>
            </div>
            <div className={`stock-tag ${availableQuantity && availableQuantity > 0 ? 'is-in-stock' : 'is-out'}`}>
              {availableQuantity && availableQuantity > 0 ? `${availableQuantity} left` : 'Out of Stock'}
            </div>
          </div>
          <div className="divider-line" />
        </>
      )}

      <div className="card-header">
        <h3 className="card-title">Volume Discounts</h3>
        <span className="card-subtitle">Save more with larger quantities</span>
      </div>

      <div className="pricing-grid">
        <div className="grid-labels">
          <span>Quantity</span>
          <span>Unit Price</span>
          <span className="text-right">Estimated Total</span>
        </div>
        
        <div className="grid-rows">
          {priceTiers.map((tier, index) => {
            const isCurrentTier = currentQuantity >= tier.minQty && 
              (!tier.maxQty || currentQuantity <= tier.maxQty);
            
            // Calculate total for the min quantity of that tier
            const totalPrice = tier.price * tier.minQty;
            
            return (
              <div 
                key={index} 
                className={`grid-row ${isCurrentTier ? 'is-active' : ''}`}
              >
                <div className="col-qty">
                  <span className="qty-label">
                    {tier.minQty}{tier.maxQty ? ` – ${tier.maxQty}` : '+'}
                  </span>
                  {isCurrentTier && <span className="active-badge">Current</span>}
                </div>
                
                <div className="col-price">
                  ₹{tier.price.toLocaleString()}
                </div>
                
                <div className="col-total text-right">
                  ₹{totalPrice.toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BulkPricingSection;